import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import { type Observable, of } from 'rxjs';

import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';

import { type EngineState } from '../../../+state/engine';
import {
  AbstractAiElementHandler,
  type ElementContext,
  getElement,
  getInitialProps,
  patchTitle,
  resolveFullWidth,
  upsertElement,
} from '../../../ai';

import { type ChartContext, type ChartResolvedContext, type ChartType } from './chart.definition';

export abstract class ChartElementHandler<T extends ChartType> extends AbstractAiElementHandler<T> {
  protected readonly store = inject(Store);

  abstract readonly flipXY: boolean;

  prepare(context: ChartContext<T>): Observable<ChartResolvedContext<T>> {
    const workflowContext = this.resolveWorkflowContext(context);

    if (!workflowContext?.workflow) {
      return of(context);
    }

    return of({
      ...context,
      workflowContext,
    });
  }

  process(context: ChartResolvedContext<T>, draft: WritableDraft<EngineState>): void {
    const props = this.getChartProps(context, draft);

    const result = upsertElement(draft, context as ElementContext<T>, props);
    const element = getElement(draft, result.elementId, this.type);

    element.width = resolveFullWidth(context.element.width);

    if (context.element.height) {
      element.height = {
        mode: 'value',
        value: context.element.height,
        fixed: true,
      };
    }
  }

  finalize(element: CanvasElementFrom<T>, context: ChartResolvedContext<T>): Observable<void> {
    return context.workflowContext
      ? this.finalizeSourceWorkflow(element, context.workflowContext)
      : of(undefined);
  }

  private getChartProps(
    context: ChartResolvedContext<T>,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<ChartType> {
    const props = getInitialProps<ChartType>(draft, context.element.id, {
      content: {
        chart: {
          xDescription: this.flipXY ? context.element.yAxisLabel : context.element.xAxisLabel,
          yDescription: this.flipXY ? context.element.xAxisLabel : context.element.yAxisLabel,
        },
      },
    });

    return patchTitle<ChartType>(props, context.element.title, context.element.showTitle);
  }
}
